import { useAuthStore } from '../stores/auth.store';
import type { ApiResponse } from '@task-tracker/shared';
import { refreshSessionOrClear } from './auth-session';

const API_BASE = import.meta.env.VITE_API_URL ?? '';

/** Error thrown by apiClient on a non-OK response, carrying the HTTP status and
 *  AppError code so callers can branch (e.g. silently re-sync on a 409). */
export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

function getAuthHeaders(): HeadersInit {
  const token = useAuthStore.getState().session?.access_token;
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

/**
 * Authenticated request with the one-shot session-refresh retry.
 *
 * The transport half of apiClient, split out so downloads can share it: a PDF
 * or a blob cannot go through apiClient, which consumes the body as JSON, and
 * hand-rolling a fetch instead (as the form-PDF download used to) silently
 * loses the 401 refresh — every download failing once the access token expires.
 *
 * Returns the parsed error body alongside the response rather than leaving the
 * caller to re-read it: deciding whether a 401 is recoverable means reading the
 * body, and a response body can only be read once. On a successful response the
 * body is left untouched, which is what lets the caller take it as a blob.
 */
export async function authedRequest(path: string, options: RequestInit = {}, attempt = 0) {
  const headers = getAuthHeaders();
  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: { ...headers, ...options.headers },
  });

  if (response.ok) return { response, errorBody: {} };

  const errorBody = await response.json().catch(() => ({ message: 'Network error' }));

  const isRecoverableAuthFailure =
    response.status === 401 &&
    attempt === 0 &&
    (errorBody.error === 'stale_session' || errorBody.error === 'token_expired');

  if (isRecoverableAuthFailure) {
    const recovery = await refreshSessionOrClear();
    if (recovery.status === 'fresh' && recovery.session.access_token) {
      return authedRequest(path, options, attempt + 1);
    }
  }

  return { response, errorBody };
}

export async function apiClient<T>(
  path: string,
  options: RequestInit = {},
): Promise<ApiResponse<T>> {
  const { response, errorBody: body } = await authedRequest(path, options);

  if (!response.ok) {
    // AppError format: { error: "code", message: "..." }
    // Zod validation format: { success: false, error: { issues: [{ message, path }] } }
    const message =
      body.message ??
      body.error?.issues
        ?.map((i: { message: string; path: string[] }) =>
          i.path?.length ? `${i.path.join('.')}: ${i.message}` : i.message,
        )
        .join('; ') ??
      `HTTP ${response.status}`;
    throw new ApiError(
      message,
      response.status,
      typeof body.error === 'string' ? body.error : undefined,
    );
  }

  const json = await response.json();
  return { data: json };
}
